import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { Input } from "../components/ui/Input";
import { PublicPageFrame } from "../components/layout/PublicPageFrame";

const cardClass =
  "bg-white/90 backdrop-blur-sm rounded-2xl shadow-2xl p-8 w-full max-w-md";

export function ResetearPasswordPage() {
  const [searchParams] = useSearchParams();
  const tokenFromUrl = searchParams.get("token") ?? "";

  const [password, setPassword] = useState("");
  const [confirmar, setConfirmar] = useState("");
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!tokenFromUrl) setError("Enlace no válido");
  }, [tokenFromUrl]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) {
      setError("La contraseña debe tener al menos 8 caracteres");
      return;
    }
    if (password !== confirmar) {
      setError("Las contraseñas no coinciden");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/auth/resetear-password", {
        token: tokenFromUrl,
        password_nueva: password,
      });
      setSuccess(true);
    } catch (err: unknown) {
      const data =
        err && typeof err === "object" && "response" in err
          ? (err as { response?: { data?: { error?: string } } }).response?.data
          : null;
      setError(data?.error ?? "Error al restablecer la contraseña");
    } finally {
      setSubmitting(false);
    }
  };

  if (!tokenFromUrl) {
    return (
      <PublicPageFrame>
        <div className={`${cardClass} text-center`}>
          <h1 className="text-xl font-semibold text-[#2C2C2C]">Enlace no válido</h1>
          <p className="mt-4 text-gray-600">
            Este enlace de recuperación no es válido o ha caducado. Solicita uno nuevo.
          </p>
          <Link
            to="/login"
            className="mt-6 inline-block font-medium text-[#1A3AB8] hover:underline"
          >
            Volver al inicio de sesión
          </Link>
        </div>
      </PublicPageFrame>
    );
  }

  if (success) {
    return (
      <PublicPageFrame>
        <div className={`${cardClass} text-center`}>
          <h1 className="text-xl font-semibold text-[#2C2C2C]">¡Contraseña actualizada!</h1>
          <p className="mt-4 text-gray-600">Ya puedes iniciar sesión con tu nueva contraseña.</p>
          <Link
            to="/login"
            className="mt-6 inline-block rounded-lg bg-[#1A3AB8] px-4 py-2.5 font-medium text-white transition hover:bg-[#15308a]"
          >
            Iniciar sesión
          </Link>
        </div>
      </PublicPageFrame>
    );
  }

  return (
    <PublicPageFrame>
      <div className={cardClass}>
        <h1 className="mb-6 text-xl font-semibold text-[#2C2C2C]">Nueva contraseña</h1>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Input
              id="password"
              label="Nueva contraseña"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={8}
              placeholder="Mínimo 8 caracteres"
              autoComplete="new-password"
            />
          </div>
          <div>
            <Input
              id="confirmar"
              label="Confirmar contraseña"
              type="password"
              value={confirmar}
              onChange={(e) => setConfirmar(e.target.value)}
              required
              minLength={8}
              placeholder="Repite la contraseña"
              autoComplete="new-password"
            />
          </div>
          {error && <p className="text-sm text-red-600">{error}</p>}
          <button
            type="submit"
            disabled={submitting}
            className="w-full rounded-lg bg-[#1A3AB8] px-4 py-2.5 font-medium text-white transition hover:bg-[#15308a] disabled:opacity-50"
          >
            {submitting ? "Guardando…" : "Restablecer contraseña"}
          </button>
        </form>
        <p className="mt-4 text-center text-sm text-gray-600">
          <Link to="/login" className="font-medium text-[#1A3AB8] hover:underline">
            Volver al inicio de sesión
          </Link>
        </p>
      </div>
    </PublicPageFrame>
  );
}
